import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/InvoicesService';
import { useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';
import { useParams } from 'react-router-dom';

export const useInvoices = (context: 'customer' | 'reports' = 'customer', customerIdProp?: string, subscriptionIdProp?: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerIdFromRoute } = useParams();
    // FNP-322: callers embedding the invoices list outside `/customers/:id`
    // (e.g. customer-subscriptions detail page) pass customerIdProp explicitly,
    // since useParams().id resolves to a different entity's id in that context.
    // FNP-329: subscriptionIdProp takes precedence — when set, the list is
    // filtered to invoices for that subscription only.
    const customerId = customerIdProp ?? customerIdFromRoute;

    const sort = { column: 'created', direction: 'desc' as const };
    let filter: { [k: string]: string } | undefined;
    if (context === 'customer') {
        if (subscriptionIdProp) {
            filter = { subscriptionId: subscriptionIdProp };
        } else if (customerId) {
            filter = { customerId };
        }
    }

    // INFINITE QUERY
    const invoicesQuery = useInfiniteQuery({
        queryKey: ['invoices', keyword, limit, sort.column, sort.direction, customerId, subscriptionIdProp],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, filter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Invoice deleted');
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        if (invoicesQuery.hasNextPage) {
            invoicesQuery.fetchNextPage();
        }
    };

    const reloadRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = invoicesQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: invoicesQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: invoicesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reloadRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};